import { getRegistrationStep, sendChooseOriginalPhone } from 'api';
import { AUTH_METHOD, REGISTRATION_TYPE, ROUTE_NAMES, SESSION_PHONE_NUMBER, RegistrationTypeEnum } from '../constants';
import translation from 'i18n/translation.json';
import { type UserInfo } from 'interfaces';
import type { ConfirmOTPResponse, PasswordFormValues } from 'interfaces/connectionType';
import { AUTH_FLOWS, REG_BLOCK_REASONS } from 'pages/Login/utils/constants';
import { getBlockReasonNavigation, handlePhoneNumberUpdate, handleRegType } from 'pages/Login/utils/helpers';
import {
  persistOtpRemainingFromApiResponse,
  resolveOtpAxiosRateLimitError,
} from 'pages/Login/utils/otpRemainingStorage';
import {
  markOtpAuthRateLimitSession,
  parseSessionPhoneRaw,
  patchSessionPhoneSender,
} from 'pages/Login/utils/sessionPhoneStorage';
import { storageAuthTokens } from 'pages/Login/utils/tokenStorage';
import { clearLoginOtpSessionTtl } from 'pages/Login/utils/loginSessionTtl';
import { type UseFormSetError } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getRouteFromStepName } from 'routes/getRouteFromStepName';
import { isV3OrV4RegistrationFlow } from 'utils/registrationStep';
import { useAppDispatch } from 'store/hooks';
import { setLoginLoader, setSkipLoanSlugFetch } from 'store/slices/ui';
import { updateUserAction, updateUserAuthentication } from 'store/slices/user';
import {
  GARegisterEvent,
  getFullFormattedNumber,
  getStorageItem,
  removeStorageItem,
  setRegTypeItem,
  setStorageItem,
  skipPinDialogForCurrentSession,
} from 'utils';

export const useAuthentication = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const onAuth = async (
    response: ConfirmOTPResponse,
    userUuid: string | null,
    setLoading: ((value: boolean) => void) | null,
    userInfo: UserInfo,
    setError: UseFormSetError<PasswordFormValues> | undefined,
    isCallVerif?: boolean,
    callback?: any, // TODO: Fix any
  ) => {
    const isRegistration = userInfo.auth_flow === AUTH_FLOWS.REG;
    const regTypeGa =
      response.registration_modes === RegistrationTypeEnum.Auto
        ? RegistrationTypeEnum.Quick
        : RegistrationTypeEnum.Manual;

    storageAuthTokens(response, userUuid);
    dispatch(
      updateUserAction({
        user: { user_uuid: response.user_uuid, session_uuid: response.session_uuid },
      }),
    );

    // NOTE: shouldSkipLoanSlugFetch - is used to avoid double call for short link
    // used here in case user visisted short link, clicked on logo and logged in via phone number
    dispatch(setSkipLoanSlugFetch(false));
    try {
      const { data: stepData } = await getRegistrationStep();
      const isDuplicateOriginLogin = stepData.registration_block_reason === REG_BLOCK_REASONS.NEED_LOGIN_ORIGIN_ACC;

      // NOTE: the user finishes registration inside this session, so the create-PIN promo
      // is postponed until the next one
      if (stepData.step_name !== ROUTE_NAMES.PersonalAccount) {
        skipPinDialogForCurrentSession();
      }

      if (isCallVerif) {
        dispatch(setLoginLoader(true));
      }
      handleRegType(stepData, regTypeGa, dispatch, setRegTypeItem);
      const blockReasonRoute = getBlockReasonNavigation(stepData);
      if (blockReasonRoute) {
        navigate(getRouteFromStepName(blockReasonRoute), { replace: true });
        if (isCallVerif) {
          dispatch(setLoginLoader(false));
        }
        setLoading?.(false);
        return;
      }

      handlePhoneNumberUpdate(stepData, isCallVerif ? () => dispatch(setLoginLoader(false)) : callback);
      GARegisterEvent({
        event: 'phone_confirmation',
        label: isRegistration ? 'sign_up' : 'login',
        type: 'sms_code',
        UID: response.user_uuid,
      });
      dispatch(updateUserAuthentication(true));
      dispatch(
        updateUserAction({
          user: {
            ...stepData.step_data,
            registration_block_reason: stepData?.registration_block_reason,
            phone_number: stepData.personal_info?.phone_number,
          },
        }),
      );
      if (isRegistration) {
        setStorageItem(REGISTRATION_TYPE, 'manual');
      }
      removeStorageItem(AUTH_METHOD);

      if (isDuplicateOriginLogin) {
        clearLoginOtpSessionTtl();
        const parsedPhone = parseSessionPhoneRaw(getStorageItem(SESSION_PHONE_NUMBER) ?? '');
        const limitsPhone = getFullFormattedNumber(parsedPhone.value ?? '');

        if (limitsPhone) {
          try {
            const chooseOriginResponse = await sendChooseOriginalPhone('sms');
            persistOtpRemainingFromApiResponse(limitsPhone, chooseOriginResponse);
            patchSessionPhoneSender('sms');
            dispatch(updateUserAction({ user: { otp_sender_channel: 'sms' } }));
          } catch (error) {
            const rateLimitError = resolveOtpAxiosRateLimitError(error, limitsPhone, 'sms');

            if (rateLimitError) {
              markOtpAuthRateLimitSession(parsedPhone.value ?? limitsPhone, 'sms');
              dispatch(updateUserAction({ user: { otp_sender_channel: 'sms' } }));
            }
          }
        }

        if (isCallVerif) {
          dispatch(setLoginLoader(false));
        }
        setLoading?.(false);
        return;
      }

      setLoading?.(false);
      if (isV3OrV4RegistrationFlow(stepData.registration_flow) && stepData.step_name === ROUTE_NAMES.BankInfo) {
        navigate(getRouteFromStepName(ROUTE_NAMES.ConfirmApplication), { replace: true });
      } else {
        navigate(getRouteFromStepName(stepData.step_name), { replace: true });
      }
    } catch (error: any) {
      GARegisterEvent({
        event: 'error_data',
        label: isRegistration ? 'sign_up' : 'login',
        stage: isRegistration ? 'phone_confirmation' : 'sms_code',
        field_name: 'password',
        error_text: `error_backend: ${error.response?.data?.detail}`,
        UID: userUuid ?? '',
      });

      setError?.(
        'password',
        {
          type: 'custom',
          message: translation.errors.incorrectCode,
        },
        { shouldFocus: true },
      );

      setLoading?.(false);
    }
  };

  return { onAuth };
};
